/**
 * GenericTraining — /project/:id/training
 * Housing shell standard. Sector-aware learning content.
 * Final tab in the project workspace navigation.
 */
import React, { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { RefreshCw, ArrowLeft, BookOpen, ChevronDown, ChevronUp, ExternalLink } from "lucide-react";
import { useProjectWorkspace } from "@/hooks/useProjectWorkspace";

import { PageHeader, SectionCard, MetricCard, CTAButton } from "@/components/ds";
// ── Sector training content ───────────────────────────────────────────────────
const TRAINING_CONTENT = {
  biogas: {
    intro: "Core climate literacy for biogas / waste-to-energy project development. Covers methodology foundations, carbon accounting, and deal structuring.",
    modules: [
      {
        id: "ipcc-waste",
        title: "IPCC 2006 Guidelines — Waste Sector (Vol. 5)",
        level: "Foundation",
        duration: "30 min",
        summary: "The authoritative source for waste sector GHG accounting. Covers landfill CH4 generation (first-order decay model), DOC factors, MCF by disposal type, and the simplified tier methodology used in DCI-BIOGAS-V1.0.",
        key_concepts: ["Degradable Organic Carbon (DOC)", "Methane Correction Factor (MCF)", "GWP CH4 = 25 (AR4)", "First-order decay model"],
        source: "IPCC 2006 National GHG Inventories, Volume 5 (Waste)",
      },
      {
        id: "ams-iiid",
        title: "AMS-III.D — Avoidance of Methane from Biomass Decay",
        level: "Methodology",
        duration: "45 min",
        summary: "CDM small-scale methodology for biogas projects. Covers baseline determination, additionality, monitoring, and leakage. The closest registered methodology to DCI-BIOGAS-V1.0.",
        key_concepts: ["Additionality demonstration", "Baseline waste scenario", "Fugitive emissions accounting", "Monitoring plan structure"],
        source: "UNFCCC CDM Methodology Booklet",
      },
      {
        id: "ams-id",
        title: "AMS-I.D — Grid Electricity via Renewable Energy",
        level: "Methodology",
        duration: "30 min",
        summary: "CDM methodology for electricity generation from renewables including biogas. Covers grid emission factor application, self-consumption vs export, and additionality for small-scale power.",
        key_concepts: ["Grid emission factor (GEF)", "Self-consumption vs export", "Combined heat and power", "Small-scale bundling"],
        source: "UNFCCC CDM Methodology Booklet",
      },
      {
        id: "dscr-fi",
        title: "DSCR and DFI Financing Structures",
        level: "Finance",
        duration: "25 min",
        summary: "Debt Service Coverage Ratio fundamentals for climate infrastructure. Covers loan annuity formula, DFI threshold (1.2–1.25x), blended finance structures, and grant component logic.",
        key_concepts: ["DSCR = EBITDA / Annual Debt Service", "DFI minimum 1.2–1.25x", "Blended finance (grant + concessional)", "Restructuring levers"],
        source: "DeCarbonIQ Deal Structuring Framework",
      },
      {
        id: "poa-framework",
        title: "Programme of Activities (PoA) Framework",
        level: "Advanced",
        duration: "40 min",
        summary: "How to aggregate multiple waste projects into a single PoA for carbon market access. Covers CME structure, CPA design, and VERRA/Gold Standard PoA registration requirements.",
        key_concepts: ["Coordinating & Managing Entity (CME)", "Component Project Activity (CPA)", "Standardized baseline approach", "PoA design document"],
        source: "VERRA VCS Programme of Activities Guidance",
      },
    ],
  },
  solar: {
    intro: "Core climate literacy for solar PV / renewable energy project development.",
    modules: [
      {
        id: "ams-id-solar",
        title: "AMS-I.D — Grid Electricity via Renewable Energy",
        level: "Foundation",
        duration: "30 min",
        summary: "CDM methodology for solar PV and other renewable energy projects. Grid displacement, additionality, and monitoring.",
        key_concepts: ["Grid emission factor", "Capacity factor", "Self-consumption accounting", "Additionality"],
        source: "UNFCCC CDM Methodology Booklet",
      },
      {
        id: "iea-grid",
        title: "IEA Grid Emission Factors",
        level: "Foundation",
        duration: "20 min",
        summary: "How national grid emission factors are computed and published. Understanding variability, marginal vs average factors, and Africa-specific considerations.",
        key_concepts: ["Marginal vs average EF", "IEA World Energy Balances", "Nigeria 0.43 kgCO2e/kWh", "Annual updates"],
        source: "IEA World Energy Balances 2023",
      },
    ],
  },
};

const LEVEL_COLORS = {
  Foundation:  "bg-emerald-100 text-emerald-700 border-emerald-200",
  Methodology: "bg-blue-100 text-blue-700 border-blue-200",
  Finance:     "bg-amber-100 text-amber-700 border-amber-200",
  Advanced:    "bg-purple-100 text-purple-700 border-purple-200",
};

export default function GenericTraining() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [expanded, setExpanded] = useState(null);
  const { loading, sectorLabel, org, sector } = useProjectWorkspace(id);

  if (loading) return (
    <div className="flex items-center justify-center min-h-[40vh]">
      <RefreshCw size={18} className="animate-spin text-teal-500" />
    </div>
  );

  const sectorKey = sector === "waste_recovery" ? "biogas" : (sector || "biogas");
  const content = TRAINING_CONTENT[sectorKey] || TRAINING_CONTENT["biogas"];
  const modules = content.modules || [];

  return (
    <div className="p-6 lg:p-8 space-y-6">

        <button onClick={() => navigate(`/project/${id}/workspace`)}
          className="flex items-center gap-1.5 text-xs text-slate-500 hover:text-slate-700 mb-5">
          <ArrowLeft size={13} /> {sectorLabel}
        </button>

          
      <PageHeader
        label="{org.name}"
        title="Climate Training"
      />

        {/* Intro */}
        <div className="bg-[#0F172A] rounded-xl px-5 py-4 mb-5">
          <div className="flex items-center gap-2 mb-2">
            <BookOpen size={14} className="text-teal-400" />
            <p className="text-xs font-bold text-white">Learning Path</p>
          </div>
          <p className="text-xs text-slate-400 leading-relaxed">{content.intro}</p>
        </div>

        {/* Module list */}
        <div className="space-y-3">
          {modules.map((m, i) => (
            <div key={m.id} className="bg-white border border-slate-200 rounded-xl overflow-hidden"
              data-testid={`module-${m.id}`}>
              <button
                className="w-full px-5 py-4 flex items-start justify-between gap-3 text-left hover:bg-slate-50"
                onClick={() => setExpanded(expanded === m.id ? null : m.id)}>
                <div className="flex items-start gap-3 flex-1 min-w-0">
                  <div className="w-7 h-7 rounded-lg bg-slate-100 flex items-center justify-center flex-shrink-0">
                    <span className="text-xs font-bold text-slate-600">{i+1}</span>
                  </div>
                  <div className="min-w-0">
                    <p className="text-xs font-bold text-slate-900">{m.title}</p>
                    <div className="flex items-center gap-2 mt-0.5">
                      <span className={`text-xs font-bold px-1.5 py-0.5 rounded-full border ${LEVEL_COLORS[m.level] || LEVEL_COLORS.Foundation}`}>
                        {m.level}
                      </span>
                      <span className="text-xs text-slate-400">{m.duration}</span>
                    </div>
                  </div>
                </div>
                {expanded === m.id
                  ? <ChevronUp size={13} className="text-slate-400 flex-shrink-0 mt-0.5" />
                  : <ChevronDown size={13} className="text-slate-400 flex-shrink-0 mt-0.5" />}
              </button>
              {expanded === m.id && (
                <div className="px-5 pb-4 pt-2 border-t border-slate-100">
                  <p className="text-xs text-slate-600 leading-snug mb-3">{m.summary}</p>
                  <div className="mb-3">
                    <p className="text-xs text-slate-400 font-semibold uppercase tracking-wide mb-1.5">Key Concepts</p>
                    <div className="flex flex-wrap gap-1.5">
                      {m.key_concepts.map(c => (
                        <span key={c} className="text-xs bg-slate-100 text-slate-600 px-2 py-0.5 rounded-full">{c}</span>
                      ))}
                    </div>
                  </div>
                  <div className="flex items-center gap-1.5 text-xs text-slate-400">
                    <ExternalLink size={9} />
                    <span>{m.source}</span>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>

        <p className="text-xs text-slate-400 italic text-center mt-6">
          Training content reflects the methodologies used in DeCarbonIQ calculations.
          For formal project validation, engage an accredited independent expert.
        </p>
    </div>
  );
}
